import { Building2, ChevronDown, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuth } from '@/lib/AuthProvider'
import { useCompany } from '@/lib/CompanyProvider'
import { supabase } from '@/integrations/supabase/client'

export function CompanySwitcher() {
  const { activeCompanyId, setActiveCompanyId, companies, isLoading, isSwitching } = useCompany()
  const { user } = useAuth()

  const role = user?.app_metadata?.role || user?.user_metadata?.role || 'COLABORADOR'
  const isSuperadmin = role === 'SUPERADMIN'

  if (isLoading || isSwitching) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        className="w-full justify-between bg-slate-900 border-slate-700 text-slate-400"
      >
        {isSwitching ? 'Trocando filial...' : 'Carregando...'}
      </Button>
    )
  }

  if (companies.length === 0) {
    return null
  }

  const activeCompany = companies.find((c) => c.id === activeCompanyId) || companies[0]

  if (isSuperadmin) {
    return (
      <Button
        variant="outline"
        size="sm"
        disabled
        className="w-full justify-start bg-slate-900 border-slate-700 text-slate-200 opacity-100 cursor-default hover:bg-slate-900"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Building2 size={16} className="text-amber-500 shrink-0" />
          <span className="truncate">{activeCompany?.name}</span>
        </div>
      </Button>
    )
  }

  const handleCreateWorkspace = async () => {
    const name = window.prompt('Nome da Nova Filial/Lava-Jato:')
    if (!name || name.trim().length === 0) return

    try {
      const { data: newCompanyId, error } = await supabase.rpc('create_workspace', { p_name: name })
      if (error) throw error
      
      // Muda o contexto ativo para o novo workspace imediatamente
      if (newCompanyId) {
        await setActiveCompanyId(newCompanyId)
      }
    } catch (err: any) {
      console.error(err)
      alert('Erro ao criar filial: ' + err.message)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-between bg-slate-900 border-slate-700 text-slate-200 overflow-hidden"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Building2 size={16} className="text-slate-400 shrink-0" />
            <span className="truncate">{activeCompany?.name}</span>
          </div>
          <ChevronDown size={16} className="opacity-50 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-full min-w-[200px] bg-slate-800 border-slate-700 text-slate-200"
      >
        {companies.map((company) => (
          <DropdownMenuItem
            key={company.id}
            onClick={() => setActiveCompanyId(company.id)}
            className={`cursor-pointer ${company.id === activeCompanyId ? 'bg-slate-700 font-bold' : 'hover:bg-slate-700/50'}`}
          >
            {company.name}
          </DropdownMenuItem>
        ))}
        
        <DropdownMenuSeparator className="bg-slate-700" />
        
        <DropdownMenuItem 
          onClick={handleCreateWorkspace}
          className="cursor-pointer text-blue-400 hover:text-blue-300 hover:bg-blue-900/20"
        >
          <Plus size={16} className="mr-2" />
          Criar Nova Filial
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
